import type {
    AuthorizerSelectionConfig,
    Authorizer,
    ExtendedFetch,
    UserInfo} from '@bayer-int/portletce-shared';
import {
    selectAuthService,
    isNotNullable,
} from '@bayer-int/portletce-shared';
import type { Observable } from 'rxjs';
import { defer } from 'rxjs';

import { createUnknownError, createAuthError } from 'portlet/common/errors';

import type { AuthorizationIntegration } from './AuthIntegrations';

export class AuthService {
    constructor(
        private readonly authorizer: Authorizer,
        private readonly authorizerConfig: AuthorizerSelectionConfig,
    ) {}

    public getUserInfo(): Observable<UserInfo> {
        return defer(() => this.authorizer.getUserInfo());
    }

    public asFetch(): ExtendedFetch {
        return this.authorizer.makeRequest;
    }

    public static configure(
        config: AuthorizerSelectionConfig,
        integrations: AuthorizationIntegration[] = [],
    ): AuthService {
        const configWithIntegrations = AuthService.applyIntegrationsToConfig(config, integrations);

        return new AuthService(
            selectAuthService({
                ...configWithIntegrations,
                errorFormatter: {
                    httpError: err => createUnknownError(err, err.message),
                    invalidToken: err => createAuthError(err, err.message),
                    // leaving the original errors untouched, because it may be inspected by aclib
                    // (or any downstream library to which we pass the `fetch`)
                    authorizationFailed: err => err,
                    requestAborted: err => err,
                },
            }),
            configWithIntegrations,
        );
    }

    public static applyIntegrationsToConfig(
        config: AuthorizerSelectionConfig,
        integrations: AuthorizationIntegration[],
    ): AuthorizerSelectionConfig {
        const tokenDomainWhitelist = [
            ...(config.tokenDomainWhitelist || []),
            ...integrations.flatMap(integration => integration.idTokenWhiteList()),
        ];

        const accessTokenDomainWhitelist = [
            ...(config.accessTokenDomainWhitelist || []),
            ...integrations.flatMap(integration => integration.accessTokenWhiteList()),
        ];

        const modifiers = integrations
            .map(integration => integration.asModifier())
            .filter(isNotNullable);

        return { ...config, tokenDomainWhitelist, accessTokenDomainWhitelist, modifiers };
    }
}
